import React, { useState } from 'react';
import ChartRenderer from './ChartRenderer';

interface ChartWithTableToggleProps {
  title: string;
  chartType: 'bar' | 'line' | 'pie' | 'table';
  data: Array<Record<string, any>>;
  xKey?: string;
  yKey?: string;
  loading?: boolean;
}

const ChartWithTableToggle: React.FC<ChartWithTableToggleProps> = ({
  title,
  chartType,
  data,
  xKey,
  yKey,
  loading,
}) => {
  const [viewMode, setViewMode] = useState<'chart' | 'table'>('chart');

  // Extract column names from data
  const columns = data.length > 0 ? Object.keys(data[0]) : [];

  return (
    <div>
      {/* Header with Title and Toggle */}
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-lg font-medium text-gray-100">{title}</h3>

        {/* Toggle Buttons */}
        <div className="inline-flex rounded-lg bg-gray-800 p-0.5">
          <button
            onClick={() => setViewMode('chart')}
            className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
              viewMode === 'chart'
                ? 'bg-blue-600 text-white'
                : 'text-gray-400 hover:text-gray-200'
            }`}
          >
            <div className="flex items-center gap-1.5">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
              Chart
            </div>
          </button>
          <button
            onClick={() => setViewMode('table')}
            className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
              viewMode === 'table'
                ? 'bg-blue-600 text-white'
                : 'text-gray-400 hover:text-gray-200'
            }`}
          >
            <div className="flex items-center gap-1.5">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M3 14h18m-9-4v8m-7 0h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
              Table
            </div>
          </button>
        </div>
      </div>

      {/* Content */}
      {viewMode === 'chart' ? (
        <ChartRenderer
          chartType={chartType}
          data={data}
          xKey={xKey}
          yKey={yKey}
          loading={loading}
        />
      ) : (
        <div className="bg-gray-800 rounded-lg border border-gray-700 overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-gray-400">Loading...</div>
          ) : data.length === 0 ? (
            <div className="p-8 text-center text-gray-400">No data available</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-700">
                    {columns.map((col) => (
                      <th
                        key={col}
                        className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider"
                      >
                        {col.replace(/_/g, ' ')}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-700">
                  {data.map((row, idx) => (
                    <tr key={idx} className="hover:bg-gray-750 transition-colors">
                      {columns.map((col) => (
                        <td key={col} className="px-4 py-3 text-sm text-gray-200">
                          {typeof row[col] === 'number'
                            ? row[col].toLocaleString()
                            : row[col] || '-'}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ChartWithTableToggle;
